import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { COMPOSER } from "@/constants/testIds";
import { toast } from "sonner";
import { ImagePlus, X, Loader2 } from "lucide-react";

export default function ComposerModal({ open, onOpenChange, onCreated }) {
  const [text, setText] = useState("");
  const [communityId, setCommunityId] = useState("none");
  const [communities, setCommunities] = useState([]);
  const [busy, setBusy] = useState(false);
  const [mediaUrl, setMediaUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [explicit, setExplicit] = useState(false);
  const fileInput = useRef(null);

  useEffect(() => {
    if (open) {
      api.get("/communities").then(({ data }) => setCommunities(data)).catch(() => {});
    } else {
      // reset on close
      setText("");
      setCommunityId("none");
      setMediaUrl("");
      setExplicit(false);
    }
  }, [open]);

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error("File too large (max 25MB)");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/uploads", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMediaUrl(data.url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const submit = async () => {
    if ((!text.trim() && !mediaUrl) || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/posts", {
        text: text.trim() || "\u200b",
        media_url: mediaUrl || null,
        community_id: communityId === "none" ? null : communityId,
        explicit,
      });
      toast.success("Posted to the vault.");
      onCreated && onCreated(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not post.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={COMPOSER.root}
        className="glass rounded-none max-w-lg border-white/10"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">What's on your mind?</DialogTitle>
          <DialogDescription className="text-zinc-500 text-sm">
            Share a moment with the vault.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          data-testid={COMPOSER.textarea}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Say something the vault hasn't heard yet…"
          className="min-h-32 bg-black/40 border-white/10 rounded-none text-base focus-visible:ring-rose-600"
        />

        {mediaUrl && (
          <div className="relative">
            {mediaUrl.match(/\.(mp4|webm)$/i) ? (
              <video src={mediaUrl} controls className="w-full max-h-72 object-cover border border-white/10" />
            ) : (
              <img src={mediaUrl} alt="attachment" className="w-full max-h-72 object-cover border border-white/10" />
            )}
            <button
              type="button"
              onClick={() => setMediaUrl("")}
              className="absolute top-2 right-2 w-8 h-8 bg-black/70 flex items-center justify-center border border-white/10 hover:border-rose-600"
              aria-label="Remove attachment"
            >
              <X size={14} />
            </button>
          </div>
        )}

        <input
          type="file"
          ref={fileInput}
          accept="image/*,video/mp4,video/webm"
          onChange={onPickFile}
          className="hidden"
          data-testid="composer-file-input"
        />

        <label
          className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer select-none"
          data-testid="composer-explicit-row"
        >
          <input
            type="checkbox"
            checked={explicit}
            onChange={(e) => setExplicit(e.target.checked)}
            className="accent-rose-600 w-4 h-4"
            data-testid="composer-explicit-toggle"
          />
          Mature/suggestive content (no nudity or explicit content — hidden in the iOS app)
        </label>

        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInput.current?.click()}
              disabled={uploading}
              className="rounded-none bg-transparent border border-white/15 text-white hover:border-rose-600 gap-2"
              data-testid="composer-attach-button"
            >
              {uploading ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />}
              {uploading ? "Uploading…" : "Attach"}
            </Button>
            <Select value={communityId} onValueChange={setCommunityId}>
              <SelectTrigger
                data-testid={COMPOSER.communitySelect}
                className="w-48 bg-black/40 border-white/10 rounded-none"
              >
                <SelectValue placeholder="Post to…" />
              </SelectTrigger>
              <SelectContent className="rounded-none border-white/10 bg-zinc-950">
                <SelectItem value="none">Public feed</SelectItem>
                {communities.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            data-testid={COMPOSER.submit}
            onClick={submit}
            disabled={(!text.trim() && !mediaUrl) || busy}
            className="btn-crimson rounded-none px-6 py-5"
          >
            {busy ? "Posting…" : "Post"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
